import { ClockIcon, PlusIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { formatDateStr } from '../../../../../../common-utils/formatters';
import type { RootState } from '../../../../../store/store';
import { JWTHeader } from '../../../../../utils/JWTHeader';
import { apiFileMap } from '../../../../../utils/rnicApiFileMap';
import { showToast } from '../../../../common/Notification';
import Popup from '../../../../common/Popup';

interface FieldChange {
  field: string;
  oldValue: string;
  newValue: string;
}

interface HistoryItem {
  date: string;
  changes: FieldChange[];
}

interface CoproHistoryProps {
  coproId: string;
}

const CoproHistory: React.FC<CoproHistoryProps> = ({ coproId }) => {
  const [loading, setLoading] = useState(true);
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [showAllChanges, setShowAllChanges] = useState(false);
  const [firstSnapshotDate, setFirstSnapshotDate] = useState<string | null>(null);

  const jwt = useSelector((state: RootState) => state.auth.token);

  const getTrimestreStart = (date: Date): Date => {
    const month = date.getMonth();
    const trimestre = Math.floor(month / 3);
    return new Date(date.getFullYear(), trimestre * 3, 1);
  };

  const isCurrentTrimester = (apiDate: string, televersementDate: string | null, processedAt: string): boolean => {
    const processedDate = new Date(processedAt);
    const trimestreStart = getTrimestreStart(processedDate);

    const apiDateObj = new Date(apiDate);
    const televersementDateObj = televersementDate ? new Date(televersementDate) : null;

    return (apiDateObj >= trimestreStart) || (televersementDateObj && televersementDateObj >= trimestreStart);
  };

  interface CoproSnapshot {
    dataRNICAPI: Record<string, unknown>;
    dataRNICFile: Record<string, unknown>;
    processedAt: string;
    createdAt: string;
  }
  const compareSnapshots = (current: CoproSnapshot, previous: CoproSnapshot): FieldChange[] => {
    const changes: FieldChange[] = [];

    const useApiRnic = isCurrentTrimester(
      String(current.dataRNICAPI.datedernieremodif ?? ''),
      String(current.dataRNICAPI.details_inforattachement_dateteleversement ?? ''),
      current.processedAt,
    );

    apiFileMap.forEach(({ apiKey, fileKey, label }) => {
      const currentValue = useApiRnic ? current.dataRNICAPI[apiKey] : current.dataRNICFile[fileKey];
      const previousValue = useApiRnic ? previous.dataRNICAPI[apiKey] : previous.dataRNICFile[fileKey];

      if (currentValue !== previousValue) {
        changes.push({
          field: label,
          oldValue: String(previousValue || ''),
          newValue: String(currentValue || ''),
        });
      }
    });

    return changes;
  };

  const processSnapshots = (snapshots: CoproSnapshot[]): HistoryItem[] => {
    const history: HistoryItem[] = [];

    for (let i = 0; i < snapshots.length - 1; i++) {
      const currentSnapshot = snapshots[i];
      const previousSnapshot = snapshots[i + 1];

      const changes = compareSnapshots(currentSnapshot, previousSnapshot);

      if (changes.length > 0) {
        history.push({
          date: currentSnapshot.createdAt,
          changes,
        });
      }
    }

    return history;
  };

  const fetchSnapshots = async(id: string) => {
    try {
      const response = await fetch(`/api/copros/${id}/copro-snapshots`, JWTHeader(jwt));
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const snapshots = await response.json();
      const processedHistory = processSnapshots(snapshots);
      setHistoryItems(processedHistory);
      if (snapshots.length > 0) {
        setFirstSnapshotDate(snapshots[snapshots.length - 1].createdAt);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching snapshots :', error);
      showToast('error', 'Une erreur est survenue lors de la récupération de l\'historique de la copropriété.', 5000);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jwt) {
      fetchSnapshots(coproId);
    }
  }, [coproId, jwt]);

  if (loading) {
    return (
      <div className="animate-pulse rounded-lg bg-white p-6">
        <div className="mb-6 h-8 w-1/3 rounded bg-gray-200" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="mb-4 flex items-center space-x-4">
            <div className="h-6 w-6 rounded-full bg-gray-200" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-3/4 rounded bg-gray-200" />
              <div className="h-4 w-1/2 rounded bg-gray-200" />
            </div>
            <div className="h-4 w-1/4 rounded bg-gray-200" />
          </div>
        ))}
      </div>
    );
  }

  const displayedItems = historyItems.slice(0, 2);

  const renderHistoryItems = (items: HistoryItem[], isPopup: boolean) => (
    <ul role="list" className="space-y-6">
      {items.map((historyItem, index) => (
        <li key={index} className="relative flex gap-x-4">
          <div className="absolute top-0 -bottom-6 left-0 flex w-6 justify-center">
            <div className="w-px bg-gray-300" />
          </div>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center bg-white">
            <ClockIcon className="h-6 w-6 text-rgpd-blue" />
          </div>
          <div className="flex-auto py-0.5 text-sm leading-5 text-gray-500">
            <time dateTime={historyItem.date} className="font-medium text-gray-900">
              {formatDateStr(historyItem.date)}
            </time>
            {historyItem.changes.map((change, changeIndex) => (
              <div key={changeIndex} className="mt-2">
                <span className="font-medium text-gray-900">{change.field}</span>
                {' '}
                a été modifié
                <div className="mt-1 flex items-center gap-x-2">
                  <span className="rounded bg-red-100 px-2 py-1 text-red-700">
                    {change.oldValue}
                  </span>
                  <svg className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                  <span className="rounded bg-green-100 px-2 py-1 text-green-700">
                    {change.newValue}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </li>
      ))}
      {!isPopup && historyItems.length > 2 && (
        <li className="relative flex gap-x-4">
          <div className="absolute top-0 -bottom-6 left-0 flex w-6 justify-center">
            <div className="w-px bg-gray-300" />
          </div>
          <div className="relative flex h-8 w-6 flex-none items-center justify-center bg-white">
            <PlusIcon className="h-6 w-6 text-rgpd-blue" />
          </div>
          <div className="flex items-center py-0.5 text-sm leading-5">
            <button
              type="button"
              className="font-semibold text-rgpd-blue hover:text-rgpd-blue-dark focus:underline focus:outline-none"
              onClick={() => setShowAllChanges(true)}
            >
              Voir plus de changements
            </button>
          </div>
        </li>
      )}
      <li className="relative flex gap-x-4">
        <div className="absolute top-0 -bottom-6 left-0 flex w-6 justify-center">
          <div className="h-full w-px border-l border-dashed border-gray-300 bg-white" style={{ borderLeftWidth: '1px' }} />
        </div>
        <div className="relative flex h-6 w-6 flex-none items-center justify-center bg-white">
          <ClockIcon className="h-6 w-6 text-rgpd-blue" />
        </div>
        <div className="flex-auto py-0.5 text-sm leading-5 text-gray-500">
          <time dateTime={firstSnapshotDate || ''} className="font-medium text-gray-900">
            {firstSnapshotDate ? formatDateStr(firstSnapshotDate) : 'En cours de traitement'}
          </time>
          <div className="mt-2">
            <span className="font-medium text-gray-900">
              Premier enregistrement de la copropriété dans le suivi Déclarations Juridiques
            </span>
          </div>
        </div>
      </li>
    </ul>
  );

  return (
    <div className="rounded-lg bg-white p-6">
      {renderHistoryItems(displayedItems, false)}
      <Popup
        isOpen={showAllChanges}
        onClose={() => setShowAllChanges(false)}
        title="Historique complet des changements"
        width="80%"
        height="80%"
      >
        {renderHistoryItems(historyItems, true)}
      </Popup>
    </div>
  );
};

export default CoproHistory;
